import React, { useState, useEffect, useContext } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { DataGrid, GridActionsCellItem, useGridApiRef } from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useNavigate, useParams } from "react-router-dom";
import { Paper, CircularProgress } from "@mui/material";
import CustomPager from "../CustomPager";
import { getApplicationsCount } from "../../utils/applicationsTypesCounter";
import { convertTimeFormat } from "../../utils/timeFormatter";
import OrdersFilter from "./OrdersFilter";
import { ConfigLinksDetailsContext } from "../../Context/cofingLinksDetailsContext";
import { useTranslation } from "react-i18next";

const DesktopMyOrders = ({ appicationsData, isLoading }) => {
  const { status } = useParams(); // Get status from URL
  const [selectedStatus, setSelectedStatus] = useState(null); // Track selected filter
  const [appliedFilters, setAppliedFilters] = useState(null);
  const navigate = useNavigate();
  const { configLinks } = useContext(ConfigLinksDetailsContext);
  const hideCancelledRequests = configLinks?.hideCancelledRequests ?? false;
  const { t } = useTranslation();
  
  // Filter out cancelled orders if setting is enabled
  const baseData = hideCancelledRequests 
    ? appicationsData.filter(item => item.status?.id !== 6)
    : appicationsData;

  const handleFilterClick = (statusId) => {
    if (selectedStatus === statusId) {
      navigate(`/my-orders`); // Navigate back to default view
    } else {
      navigate(`/my-orders/${statusMap[statusId].slug}`); // Update URL with selected status
    }
  };

  const statusMap = {
    1: { name: t('status_new'), color: "#fff", borderColor: "#1976d2", slug: "new" },
    2: { name: t('status_inProgress'), color: "#FFE18C", borderColor: "#f57c00", slug: "in-progress" },
    3: { name: t('status_pending'), color: "#65E0FF", borderColor: "#0288d1", slug: "pending" },
    4: { name: t('status_completed'), color: "#72EF9B", borderColor: "#388e3c", slug: "completed" },
    5: { name: t('status_rejected'), color: "#ff000072", borderColor: "#ef5555ff", slug: "rejected" },
    6: { name: t('status_cancelled'), color: "#D1D1D1", borderColor: "#757575", slug: "canceled" },
  };

  useEffect(() => {
    // Convert status string to number if needed and update selected status
    const statusId = Object.keys(statusMap).find(
      (key) => statusMap[key].slug === status
    );

    if (statusId) {
      setSelectedStatus(Number(statusId)); // Corrected way to convert string to number
    } else {
      setSelectedStatus(null); // If not found, set to null or default
    }
  }, [status]); // Re-run whenever the `status` changes

  const columnHeader = (params) => {
    return (
      <Box>
        <Typography  sx={{ fontWeight: "bold", color: "primary.main" }}>
          {params.colDef.headerName}
        </Typography>
      </Box>
    );
  };

  const applicationsTypes = [
    { title: "طلب جديد", color: "#fff", id: 1 },
    { title: "طلب قيد التنفيذ", color: "#FFE18C", id: 2 },
    { title: "طلب معلق", color: "#65E0FF", id: 3 },
    { title: "طلب مكتمل", color: "#72EF9B", id: 4 },
    { title: "طلب مرفوض", color: "#FFB3B3", id: 5 },
    { title: "ملغى", color: "#D1D1D1", id: 6 },
  ];

  const handleViewClick = (item) => {
    navigate(`/order-details/${item.row.subscriberServiceId}`, {
      state: { ...location?.state, groupName: item.row.serviceName },
    });
  };

  const columns = [
    {
      field: "subscriberServiceFullNumber",
      headerName: t('col_orderNumber'),
      flex: 1,
      renderHeader: (params) => columnHeader(params),
      headerAlign: "center",
      headerClassName: "table-header",
      align: "center",
    },
    {
      field: "serviceName",
      headerName: t('col_requestType'),
      flex: 1,
      renderHeader: (params) => columnHeader(params),
      headerAlign: "center",
      headerClassName: "table-header",
      align: "center",
    },
    {
      field: "applicationFee",
      headerName: t('col_fees'),
      flex: 1,
      valueGetter: (obj) => `${obj.value} ${obj.row.currencyChar3Code}`,
      renderHeader: (params) => columnHeader(params),
      headerAlign: "center",
      headerClassName: "table-header",
      align: "center",
    },
    {
      field: "insertDate",
      headerName: t('col_date'),
      flex: 1,
      valueGetter: (obj) => convertTimeFormat(obj.value),
      renderHeader: (params) => columnHeader(params),
      headerAlign: "center",
      headerClassName: "table-header",
      align: "center",
    },
    {
      field: "activityName",
        headerName: t('col_activity'),
        flex: 1,
        renderHeader: (params) => columnHeader(params),
        headerAlign: "center",
        headerClassName: "table-header",
        align: "center",
      },
      {
        field: "accountForName",
        headerName: t('col_accountFor'),
        flex: 1,
        renderHeader: (params) => columnHeader(params),
        headerAlign: "center",
        headerClassName: "table-header",
        align: "center",
      },
    {
      field: "actions",
      headerName: t('col_review'),
      flex: 1,
      renderHeader: (params) => columnHeader(params),
      headerAlign: "center",
      headerClassName: "table-header",
      align: "center",
      renderCell: (obj) => (
        <GridActionsCellItem
          icon={<VisibilityIcon />}
          label="View"
          onClick={() => handleViewClick(obj)}
        />
      ),
    },
  ];

  // Filter rows based on the selected status
  const filteredData =
    selectedStatus === null
      ? baseData
      : baseData.filter((item) => item.status.id === selectedStatus);

  // Get data for counting based on applied filters
  let dataForCounting = baseData;
  if (appliedFilters) {
    dataForCounting = baseData.filter((item) => {
      let match = true;

      if (appliedFilters.fromDate) {
        match = match && new Date(item.insertDate) >= new Date(appliedFilters.fromDate);
      }
      if (appliedFilters.toDate) {
        match = match && new Date(item.insertDate) <= new Date(appliedFilters.toDate);
      }
      if (appliedFilters.serviceType) {
        match = match && item.serviceName === appliedFilters.serviceType;
      }

      return match;
    });
  }

  // Apply additional filters if any
  let finalFilteredData = filteredData;
  if (appliedFilters) {
    finalFilteredData = filteredData.filter((item) => {
      let match = true;

      if (appliedFilters.fromDate) {
        match = match && new Date(item.insertDate) >= new Date(appliedFilters.fromDate);
      }
      if (appliedFilters.toDate) {
        match = match && new Date(item.insertDate) <= new Date(appliedFilters.toDate);
      }
      if (appliedFilters.serviceType) {
        match = match && item.serviceName === appliedFilters.serviceType;
      }

      return match;
    });
  }

  const handleFilterApply = (filters) => {
    setAppliedFilters(filters);
    setSelectedStatus(null);
    navigate('/my-orders');
  };

  // Extract unique service types from data
  const uniqueServiceTypes = [...new Set(baseData.map(item => item.serviceName))];

  const apiRef = useGridApiRef();

  if (isLoading) {
    return (
      <Box sx={{ mt: 5, textAlign: "center" }}>
        <CircularProgress size={80} />
      </Box>
    );
  }

  if (!baseData || baseData.length === 0) {
    return (
      <Box sx={{ textAlign: "center", p: 5 }}>
        <Typography variant="h5" color="text.secondary" sx={{ mb: 2 }}>
          {t('noOrders')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('noOrdersDesc')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        padding: 3,
        width: "100%",
        "& .table-header": {
          backgroundColor: "#DFE7F2",
          color: "primary.main",
          fontWeight: "bold",
          border: "1px solid white !important",
        },
        "& .MuiDataGrid-columnHeaders": {
          overflowX: { xs: "scroll", md: "hidden" },
        },
        "& .MuiDataGrid-footerContainer": {
          direction: "ltr",
        },
        "& .MuiDataGrid-row:hover": {
          backgroundColor: "#d9e0eb",
        },
      }}
    >
      {/* Filter Component */}
      <OrdersFilter onFilter={handleFilterApply} isMobile={false} serviceTypes={uniqueServiceTypes} />

      {/* Application Type Summary */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          marginBottom: 3,
          justifyContent: "center",
        }}
      >
        {Object.keys(statusMap).filter(key => !(hideCancelledRequests && Number(key) === 6)).map((key) => {
          const id = Number(key);
          const { name, color, borderColor } = statusMap[key];
          const applicationCount = getApplicationsCount(dataForCounting, id);

          return (
            <Paper
              key={id}
              onClick={() => handleFilterClick(id)}
              sx={{
                cursor: "pointer",
                padding: 2,
                minWidth: "119px",
                textAlign: "center",
                backgroundColor: color,
                borderRadius: "12px",
                border: selectedStatus === id ? `3px solid ${borderColor}` : "none",
                boxShadow: selectedStatus === id ? `0px 4px 12px ${borderColor}50` : 2,
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: `0px 6px 16px ${borderColor}60`,
                },
                "&:active": {
                  transform: "translateY(-2px)",
                },
              }}
            >
              <Typography

                sx={{fontSize: "1.85rem", fontWeight: "bold", color: borderColor }}
              >
                {applicationCount}
              </Typography>
              <Typography variant="h7" sx={{ fontWeight: 500 }}>
                {name}
              </Typography>
            </Paper>
          );
        })}
      </Box>

      {/* Data Grid */}
      {finalFilteredData.length === 0 ? (
        <Box sx={{ textAlign: "center", p: 5 }}>
          <Typography variant="h6" color="text.secondary">
            {t('noOrders')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('noOrdersDesc')}
          </Typography>
        </Box>
      ) : (
        <DataGrid
          rows={finalFilteredData.map((item, index) => ({ ...item, id: index }))}
          columns={columns}
          getRowClassName={(params) =>
            `row-${params.row.status.id}` // Dynamically apply row class
          }
          initialState={{
                      pagination: {
                          paginationModel: {
                              pageSize: 10,
                          },
                      },
                  }}

          disableRowSelectionOnClick
          autoHeight
          pageSizeOptions={[5]}
          apiRef={apiRef}
                  slots={{
                      pagination: CustomPager,
                  }}
        />
      )}
    </Box>
  );
};

export default DesktopMyOrders;

